import { Alert, Box, Fab, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { CampaignModal, Fade, IconButton, Icons } from 'utoniq-ui'

import { Maybe, TradeBoxFragment } from '@/clients/utoniq-core/schema'
import {
  BaseLayout,
  BottomSafeArea,
  Head,
  SideMenu,
  SideMenuProps,
} from '@/components/layouts'
import { TradeBox, TradeBoxSkeleton } from '@/components/organisms'
import {
  FullscreenModal,
  HorizontalList,
  ScrollFetcher,
} from '@/components/organisms/functionals'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { config } from '@/config'
import { StorageImage, useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { usePrivateAction } from '@/lib/privateAction'

export * from './fragments'

export type State = {
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  artistId: string
  artistName: string
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  user: Maybe<SideMenuProps['user']>
  isArtist: Maybe<SideMenuProps['isArtist']>
  trades: Maybe<{ edges: Array<{ node: TradeBoxFragment }> }>
  myInProgressTrades: TradeBoxFragment[]
  liveStreamId: string | undefined
}

type TradingDuringTradingCellProps = {
  myInProgressTrades: State['myInProgressTrades']
  loading: boolean
  myUserId: string
}

type EveryOneTradeCellProps = {
  loading: State['loading']
  hasNextPage: State['hasNextPage']
  trades: State['trades']
  onFetchMore: Action['onFetchMore']
  myUserId: string
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const ArtistTradeTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { routerNames } = useRouter()
  const { t } = useTranslation('artistTrade')
  const [openModal, setOpenModal] = useState(false)
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)

  return (
    <ArtistThemeProvider>
      <BaseLayout
        navigation={{
          type: 'artist',
          props: {
            isLive: !!state.liveStreamId,
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
          },
        }}
        header={{
          title: t('title'),
          right: (
            <IconButton.Help
              edge="end"
              color="primary"
              onClick={() => setOpenModal(true)}
            />
          ),
          left: state.user && config.applicationType === 'organization' && (
            <IconButton.AvatarNoImage
              edge="start"
              onClick={() => setOpenSideMenu(!openSideMenu)}
              color="icon.line"
              bgcolor="icon.background"
              badge={
                state.user?.hasUnappliedDeliveryItems ||
                state.user?.hasWaitingPaymentItems ||
                !!state.user?.unreadNotificationCount
              }
            />
          ),
        }}
      >
        <Head title={[routerNames.ArtistTrade, state.artistName]} />
        {state.user && (
          <TradingDuringTradingCell
            myInProgressTrades={state.myInProgressTrades}
            loading={state.loading}
            myUserId={state.user.id}
          />
        )}
        <EveryOneTradeCell
          loading={state.loading || state.refetching}
          hasNextPage={state.hasNextPage}
          trades={state.trades}
          onFetchMore={onFetchMore}
          myUserId={state.user?.id ?? ''}
        />
        {!state.loading && (
          <BaseCell artistId={state.artistId} isLoggedIn={!!state.user} />
        )}
        <SideMenu
          followArtists={state.followArtists}
          memberArtists={state.memberArtists}
          open={openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={state.user}
          isArtist={state.isArtist}
        />
      </BaseLayout>

      <TradeHelpModal open={openModal} onClose={() => setOpenModal(false)} />
    </ArtistThemeProvider>
  )
}

export const TradingDuringTradingCell: Cell<TradingDuringTradingCellProps> = ({
  myInProgressTrades,
  loading,
  myUserId,
}) => {
  const { t } = useTranslation('artistTrade')
  const { routerPages, generatePath } = useRouter()
  return (
    <CellFrame>
      <HorizontalList
        loading={loading}
        sx={{ mb: -6 }}
        LoadingComponent={TradeBoxSkeleton}
        loadingItemLength={5}
        title={t('newMyTradeTitle')}
        titleTypographyProps={{ variant: 'h4' }}
        items={myInProgressTrades}
        trans={t('viewTradeBox')}
        transTypographyProps={{ variant: 'button' }}
        listHref={routerPages.MyTrade}
        EmptyComponent={
          <Box sx={{ pb: 4, px: 5 }}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ py: 6 }}
              align="center"
            >
              {t('tradeEmptyMessage')}
            </Typography>
            <Alert
              icon={false}
              sx={{ bgcolor: 'grey.50', borderRadius: 2, py: 2 }}
            >
              <Typography variant="body2" color="text.secondary">
                {t('tradeEmptyDesc')}
              </Typography>
            </Alert>
          </Box>
        }
        renderItem={(trade, i) => {
          if (!trade) return

          return (
            <Fade key={i} timeout={225}>
              <TradeBox
                fullWidth={false}
                trades={trade}
                imgSize="x-small"
                href={generatePath(routerPages.MyTradeDetail, {
                  tradeNegotiationId: trade.id,
                })}
                myUserId={myUserId}
              />
            </Fade>
          )
        }}
      />
    </CellFrame>
  )
}

export const EveryOneTradeCell: Cell<EveryOneTradeCellProps> = ({
  loading,
  hasNextPage,
  trades,
  onFetchMore,
  myUserId,
}) => {
  const { t } = useTranslation('artistTrade')
  const { routerPages, generatePath } = useRouter()
  return (
    <CellFrame sx={{ px: 5 }}>
      <Typography variant="h4" sx={{ py: 2 }}>
        {t('everyOneTitle')}
      </Typography>
      <Box sx={{ py: 4 }}>
        <ScrollFetcher
          loading={loading}
          hasNextPage={hasNextPage}
          componentProps={{ spacing: 4 }}
          onFetchMore={onFetchMore}
          emptyMessage={t('tradeEmptyMessage')}
          nodes={trades?.edges.map(({ node }, i) => {
            if (!node) return <></>

            return (
              <TradeBox
                key={i}
                href={generatePath(routerPages.UserTradeDetail, {
                  tradeNegotiationId: node.id,
                })}
                myUserId={myUserId}
                fullWidth
                trades={node}
              />
            )
          })}
          loadingComponent={
            <Stack spacing={4}>
              <TradeBoxSkeleton fullWidth />
              <TradeBoxSkeleton fullWidth />
              <TradeBoxSkeleton fullWidth />
            </Stack>
          }
        />
      </Box>
    </CellFrame>
  )
}

type TradeHelpModalProps = {
  open: boolean
  onClose: () => void
}
const TradeHelpModal = ({ open, onClose }: TradeHelpModalProps) => {
  const theme = config.theme.dark ? 'dark' : 'light'
  const { t } = useTranslation('artistTrade')

  return (
    <FullscreenModal open={open} onClose={onClose}>
      <Fade>
        <BaseLayout
          header={{
            title: t('howToTrade'),
            left: <IconButton.Close edge="start" onClick={onClose} />,
          }}
        >
          <BottomSafeArea
            sx={{ bgcolor: 'background.default' }}
            defaultProps={{ sx: { bgcolor: 'grey.50' } }}
          >
            <StorageImage
              target="common"
              width="100%"
              src={`images/info/artistTradeInfo--${theme}.png`}
            />
          </BottomSafeArea>
        </BaseLayout>
      </Fade>
    </FullscreenModal>
  )
}

type BaseCellProps = {
  artistId: string
  isLoggedIn: boolean
}
const BaseCell: Cell<BaseCellProps> = ({ artistId, isLoggedIn }) => {
  const { t } = useTranslation('artistTrade')
  const { analytics } = useAnalytics()
  const { handleLoginModal } = usePrivateAction()
  const [open, setOpen] = useState<boolean>(false)
  const { routerPages, generatePath, router } = useRouter()
  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 76,
          zIndex: 5,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab
          size="medium"
          color="primary"
          onClick={handleLoginModal(() => {
            analytics?.track('click_trade', {
              applicationKey: config.applicationKey,
              artistId,
            })
            setOpen(true)
          })}
        >
          <Icons.TradeRequest fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
      <CampaignModal
        type="square"
        open={open}
        banner={
          <StorageImage
            width="100%"
            height="100%"
            src="images/info/TradeRequestInfo.png"
            target="common"
          />
        }
        onClose={() => setOpen(false)}
        content={t('tradeRequestModalContent')}
        ok={{
          title: t('tradeRequestModalOK'),
          onClick() {
            analytics?.track('click_toMyCollection', {
              applicationKey: config.applicationKey,
              artistId,
            })
            router.push(
              generatePath(routerPages.ArtistCollections, { artistId })
            )
          },
        }}
        cancel={{ title: t('tradeRequestModalCancel') }}
      />
    </Stack>
  )
}
